import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  IconButton,
  Link,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockResetIcon from '@mui/icons-material/LockReset';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { identifyInputType, validatePassword } from '../../utils/validators';
import { ForgotPasswordSentOTP, login, setNewPassword } from '../../services/backendAPI';
import OtpTimer from '../../components/Common/OtpTimer';
import { alpha } from '@mui/material/styles';
import { API_CONFIG } from "../../config/api";
import brokerConfig from '../../config/brokerConfig';
import countryCodes from '../../data/countryCodes.json';
import useGeoCountry from '../../hooks/useGeoCountry';
import { EmojiFlag } from '../Common/EmojiFlag';

const ForgotPassword = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmall = useMediaQuery('(max-width:400px)');

  // Form data
  const [formData, setFormData] = useState({
    clientId: '',
    emailOrPhone: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [forgotTab, setForgotTab] = useState('email'); // 'email' | 'phone'
  const [forgotPhoneNumber, setForgotPhoneNumber] = useState('');
  const { countryCode: forgotCountryCode, setCountryCode: setForgotCountryCode } = useGeoCountry();

  // UI states
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Data states
  const [loginId, setLoginId] = useState('');
  const [twoFAToken, setTwoFAToken] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    message: '',
  });

  // Clear localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('twofa_token');
      localStorage.removeItem('contactType');
      localStorage.removeItem('contactValue');
      localStorage.removeItem('reset_client_id');
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error
    setError('');

    // Validate password as user types
    if (name === 'newPassword') {
      setPasswordValidation(validatePassword(value));
    }

    // Check if passwords match
    if (name === 'confirmPassword' && formData.newPassword !== value) {
      setError('Passwords do not match');
    } else if (name === 'confirmPassword') {
      setError('');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpSent) {
      // --- Build payload based on active tab ---
      let payload;
      let type; // used by localStorage.setItem below
      let value; // used by localStorage.setItem below

      if (forgotTab === 'email') {
        // Email flow — keep existing identifyInputType logic
        if (!formData.emailOrPhone) {
          setError('All fields are required');
          setSnackbarMessage('All fields are required');
          setOpenSnackbar(true);
          return;
        }

        const identified = identifyInputType(formData.emailOrPhone);
        type = identified.type;
        value = identified.value;

        if (type === 'invalid') {
          setError('Please enter a valid email or phone number');
          setSnackbarMessage('Please enter a valid email or phone number');
          setOpenSnackbar(true);
          return;
        }

        payload = { channel: type };
        if (type === 'email') {
          payload.email = value;
        } else {
          payload.mobile = value;
        }

      } else {
        // Phone flow — compose country code + digits
        if (!forgotPhoneNumber.trim()) {
          setError('Please enter your phone number.');
          setSnackbarMessage('Please enter your phone number.');
          setOpenSnackbar(true);
          return;
        }

        type = 'mobile';
        value = (forgotCountryCode?.dial_code || '') + forgotPhoneNumber.trim();
        payload = { channel: 'mobile', mobile: value };
      }

      // Call API to send OTP
      try {
        setIsLoading(true);
        const response = await ForgotPasswordSentOTP(payload);

        if (response && response.status === 'success') {
          // Store the twofa_token in localStorage
          localStorage.setItem('twofa_token', response.data.twofa_token);

          // Store the client_id from API response in localStorage
          localStorage.setItem('reset_client_id', response.data.client_id);

          // Store contact type and value for resend functionality
          localStorage.setItem('contactType', type);
          localStorage.setItem('contactValue', value);

          // Set state for OTP verification
          setOtpSent(true);
          // setLoginId(formData.clientId);
          setTwoFAToken(response.data.twofa_token);
          setLoginId(response.data.client_id);
        } else {
          setError(response?.message || 'Failed to send OTP. Please try again.');
          setSnackbarMessage(response?.message || 'Failed to send OTP. Please try again.');
          setOpenSnackbar(true);
        }
      } catch (err) {
        setError('An error occurred. Please try again.');
        setSnackbarMessage(err.message || 'An error occurred. Please try again.');
        setOpenSnackbar(true);
        console.error('Forgot password error:', err);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Second screen - verify OTP and reset password
      if (!formData.otp || !formData.newPassword || !formData.confirmPassword) {
        setError('All fields are required');
        setSnackbarMessage('All fields are required');
        setOpenSnackbar(true);
        return;
      }

      // Validate OTP format (assuming it's a 4-6 digit number)
      if (!/^\d{4,6}$/.test(formData.otp)) {
        setError('Please enter a valid OTP');
        setSnackbarMessage('Please enter a valid OTP');
        setOpenSnackbar(true);
        return;
      }

      // Check password validation
      const validation = validatePassword(formData.newPassword);
      if (!validation.isValid) {
        setError(validation.message);
        setSnackbarMessage(validation.message);
        setOpenSnackbar(true);
        return;
      }

      // Check if passwords match
      if (formData.newPassword !== formData.confirmPassword) {
        setError('Passwords do not match');
        setSnackbarMessage('Passwords do not match');
        setOpenSnackbar(true);
        return;
      }

      // Call API to set new password
      try {
        setIsLoading(true);
        const payload = {
          login_id: loginId,
          otp: formData.otp,
          twofa_token: twoFAToken,
          new_password: formData.newPassword
        };

        const response = await setNewPassword(payload);

        if (response && response.status === 'success') {
          setSuccess(true);
          // Clear the twofa_token from localStorage as it's no longer needed
          localStorage.removeItem('twofa_token');
          localStorage.removeItem('contactType');
          localStorage.removeItem('contactValue');
        } else {
          setError(response?.message || 'Failed to reset password. Please try again.');
          setSnackbarMessage(response?.message || 'Failed to reset password. Please try again.');
          setOpenSnackbar(true);
        }
      } catch (err) {
        console.log("err", err);
        // Extract error message from API response
        let errorMessage = 'Password reset error occurred.';

        if (err && typeof err === 'string') {
          errorMessage = err;
        } else if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (err?.error) {
          errorMessage = err.error;
        }

        setError(errorMessage);
        setSnackbarMessage(errorMessage);
        setOpenSnackbar(true);
        console.error('Password reset error:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResendOTP = async () => {
    // Get stored contact type and value
    const contactType = localStorage.getItem('contactType');
    const contactValue = localStorage.getItem('contactValue');

    if (!contactType || !contactValue) {
      // If not stored, use the form data
      const { type, value } = identifyInputType(formData.emailOrPhone);

      if (type === 'invalid' || !value) {
        setError('Please enter a valid email or phone number');
        setSnackbarMessage('Please enter a valid email or phone number');
        setOpenSnackbar(true);
        return;
      }

      // Prepare payload based on input type
      const payload = {
        login_id: formData.clientId,
        channel: type
      };

      // Add dynamic key based on input type
      if (type === 'email') {
        payload.email = value;
      } else {
        payload.mobile = value;
      }

      sendOTP(payload);
    } else {
      // Use stored values
      const payload = {
        // login_id: loginId || formData.clientId,
        channel: contactType
      };

      // Add dynamic key based on contact type
      if (contactType === 'email') {
        payload.email = contactValue;
      } else {
        payload.mobile = contactValue;
      }

      sendOTP(payload);
    }
  };

  const sendOTP = async (payload) => {
    console.log('Sending OTP with payload:', payload);

    try {
      setIsResending(true);
      const response = await ForgotPasswordSentOTP(payload);

      if (response && response.status === 'success') {
        // Update the twofa_token in localStorage
        localStorage.setItem('twofa_token', response.data.twofa_token);

        // Update the client_id in localStorage if present in response
        if (response.data.client_id) {
          localStorage.setItem('reset_client_id', response.data.client_id);
          setLoginId(response.data.client_id);
        }

        // Update state with new token
        setTwoFAToken(response.data.twofa_token);

        // Show success message
        const tempElement = document.createElement('div');
        tempElement.innerHTML = '<div style="color: green; text-align: center; margin-top: 8px;">OTP resent successfully!</div>';
        document.querySelector('.otp-resend-message')?.appendChild(tempElement);
        setTimeout(() => {
          tempElement.remove();
        }, 3000);
      } else {
        setError(response?.message || 'Failed to resend OTP. Please try again.');
        setSnackbarMessage(response?.message || 'Failed to resend OTP. Please try again.');
        setOpenSnackbar(true);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      setSnackbarMessage(err.message || 'An error occurred. Please try again.');
      setOpenSnackbar(true);
      console.error('Resend OTP error:', err);
    } finally {
      setIsResending(false);
    }
  };

  // Add a function to mask email/phone
  const maskContactInfo = (type, value) => {
    if (!value) return '';

    if (type === 'email') {
      // Mask email: show first 2 chars, then asterisks, then @ and domain
      const [username, domain] = value.split('@');
      if (!username || !domain) return '***@***';

      const maskedUsername = username.length <= 2
        ? username
        : username.substring(0, 2) + '*'.repeat(Math.min(username.length - 2, 3));

      return `${maskedUsername}@${domain}`;
    } else if (type === 'mobile') {
      // Mask phone: show first 2 and last 2 digits, mask the rest
      if (value.length <= 4) return value;

      return value.substring(0, 2) + '*'.repeat(value.length - 4) + value.substring(value.length - 2);
    }

    return '********';
  };

  // Render success screen
  if (success) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          background: theme.palette.mode === "dark"
            ? `linear-gradient(45deg, ${alpha(theme.palette.primary.dark, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`
            : `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: { xs: "2vh", sm: "4vh" },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            mb: 4,
            textAlign: 'center'
          }}
        >
          <img
            src={brokerConfig.logoFull}
            alt={brokerConfig.title}
            style={{
              width: brokerConfig.logoDimensions?.login?.width || 160,
              height: brokerConfig.logoDimensions?.login?.height || 'auto',
              filter: theme.palette.mode === "dark" ? "none" : "none",
              objectFit: 'contain'
            }}
          />
        </Box>

        <Paper
          elevation={isMobile ? 2 : 4}
          sx={{
            width: "100%",
            maxWidth: "400px",
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '16px',
          }}
        >
          <Box sx={{
            width: isMobile ? 60 : 70,
            height: isMobile ? 60 : 70,
            borderRadius: '50%',
            bgcolor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}>
            <CheckCircleOutlineIcon sx={{ fontSize: isMobile ? 30 : 40, color: 'white' }} />
          </Box>

          <Typography color="success.main" variant="h5" sx={{ mb: 2, fontWeight: 'bold', fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
            Success!
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', fontSize: isMobile ? '0.875rem' : '1rem' }}>
            Your password has been reset successfully. You can now login with your new password.
          </Typography>

          <Button
            onClick={onBack}
            variant="contained"
            fullWidth
            size={isMobile ? "medium" : "large"}
            sx={{
              borderRadius: 2,
              py: isMobile ? 1 : 1.5,
              fontWeight: 'bold',
              background: "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)",
              "&:hover": {
                background: "linear-gradient(90deg, #1565c0 0%, #1976d2 100%)",
              },
            }}
          >
            Back to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        background: theme.palette.mode === "dark"
          ? `linear-gradient(45deg, ${alpha(theme.palette.primary.dark, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`
          : `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: { xs: "2vh", sm: "4vh" },
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          mb: 4,
          textAlign: 'center'
        }}
      >
        <img
          src={brokerConfig.logoFull}
          alt={brokerConfig.title}
          style={{
            width: brokerConfig.logoDimensions?.login?.width || 160,
            height: brokerConfig.logoDimensions?.login?.height || 'auto',
            filter: theme.palette.mode === "dark" ? "none" : "none",
            objectFit: 'contain'
          }}
        />
      </Box>

      <Paper
        elevation={isMobile ? 2 : 4}
        sx={{
          width: "100%",
          maxWidth: "400px",
          p: { xs: 3, sm: 4 },
          borderRadius: '16px',
          position: "relative",
        }}
      >
        <IconButton
          sx={{ position: 'absolute', left: 10, top: 10 }}
          onClick={onBack}
          size={isMobile ? "small" : "medium"}
        >
          <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, mt: isMobile ? 3 : 1 }}>
          {otpSent ? (
            <LockResetIcon color="primary" sx={{ fontSize: isMobile ? 24 : 32, mr: 1 }} />
          ) : (
            <MarkEmailReadIcon color="primary" sx={{ fontSize: isMobile ? 24 : 32, mr: 1 }} />
          )}
          <Typography component="h1" variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
            {otpSent ? 'Reset Password' : 'Forgot Password'}
          </Typography>
        </Box>

        {!otpSent && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            Enter your registered email/phone to receive a verification code
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          {!otpSent ? (
            // First screen - Client ID and Email/Phone
            <>
              {/* --- Tab Toggle: Email vs Phone Number --- */}
              {brokerConfig.showCountryPicker && (
                <Box
                  sx={{
                    display: 'flex',
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(0,0,0,0.06)',
                    borderRadius: '10px',
                    p: '4px',
                    gap: '4px',
                    mb: 2,
                  }}
                >
                  <Box
                    onClick={() => setForgotTab('email')}
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      py: 0.75,
                      px: 1,
                      borderRadius: '7px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      transition: 'all 0.2s ease',
                      userSelect: 'none',
                      background: forgotTab === 'email'
                        ? theme.palette.background.paper
                        : 'transparent',
                      color: forgotTab === 'email'
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      boxShadow: forgotTab === 'email'
                        ? '0 1px 4px rgba(0,0,0,0.15)'
                        : 'none',
                    }}
                  >
                    Email
                  </Box>
                  <Box
                    onClick={() => setForgotTab('phone')}
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      py: 0.75,
                      px: 1,
                      borderRadius: '7px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      transition: 'all 0.2s ease',
                      userSelect: 'none',
                      background: forgotTab === 'phone'
                        ? theme.palette.background.paper
                        : 'transparent',
                      color: forgotTab === 'phone'
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      boxShadow: forgotTab === 'phone'
                        ? '0 1px 4px rgba(0,0,0,0.15)'
                        : 'none',
                    }}
                  >
                    Phone Number
                  </Box>
                </Box>
              )}

              {/* --- Existing Card (unchanged) --- */}
              <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                {/* <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="clientId"
                  label="Client ID"
                  name="clientId"
                  autoComplete="username"
                  value={formData.clientId}
                  onChange={handleChange}
                  error={!!error}
                  placeholder="Enter your client ID"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  size={isMobile ? "small" : "medium"}
                /> */}

                {/* --- Conditional Input: Email OR Phone Number --- */}
                {forgotTab === 'email' || !brokerConfig.showCountryPicker ? (
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="emailOrPhone"
                    label="Email Address"
                    name="emailOrPhone"
                    autoComplete="email"
                    autoFocus
                    value={formData.emailOrPhone}
                    onChange={handleChange}
                    error={!!error}
                    placeholder="Enter your registered email"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                    {/* Country Code Autocomplete */}
                    <Autocomplete
                      value={forgotCountryCode}
                      onChange={(_, newValue) => newValue && setForgotCountryCode(newValue)}
                      options={countryCodes}
                      getOptionLabel={(option) => option.dial_code}
                      filterOptions={(options, { inputValue }) => {
                        const q = inputValue.toLowerCase();
                        return options.filter(
                          o =>
                            o.name.toLowerCase().includes(q) ||
                            o.dial_code.includes(q) ||
                            o.code.toLowerCase().includes(q)
                        );
                      }}
                      renderOption={(props, option) => {
                        // Extract key to avoid spreading it, fixing React 18+ strict mode warnings
                        const { key, ...otherProps } = props;
                        return (
                          <Box
                            component="li"
                            key={option.code}
                            {...otherProps}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '6px !important' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 30, justifyContent: 'center' }}>
                              <EmojiFlag emoji={option.emoji} code={option.code} />
                            </Box>
                            <span style={{ flex: 1, fontSize: '0.85rem' }}>{option.name}</span>
                            <span style={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              opacity: 0.65,
                              letterSpacing: '0.3px',
                            }}>
                              {option.dial_code}
                            </span>
                          </Box>
                        );
                      }}
                      disableClearable
                      isOptionEqualToValue={(opt, val) => opt.code === val.code}
                      ListboxProps={{ sx: { maxHeight: 240, p: 0 } }}
                      componentsProps={{
                        paper: {
                          sx: {
                            width: 280,
                            fontSize: '0.85rem',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            borderRadius: '10px',
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size={isMobile ? 'small' : 'medium'}
                          label="Code"
                          variant="outlined"
                          sx={{
                            width: 115,
                            '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start" sx={{ ml: 0.5, mr: 0, minWidth: 26, justifyContent: 'center', color: 'text.primary' }}>
                                <EmojiFlag emoji={forgotCountryCode?.emoji} code={forgotCountryCode?.code} />
                              </InputAdornment>
                            ),
                          }}
                          inputProps={{
                            ...params.inputProps,
                            style: { fontSize: '0.875rem', fontWeight: 500, paddingLeft: 0 },
                          }}
                        />
                      )}
                      sx={{ width: 115, flexShrink: 0 }}
                    />
                    {/* Phone Number Input */}
                    <TextField
                      fullWidth
                      id="forgotPhone"
                      label="Phone Number"
                      name="forgotPhone"
                      autoComplete="tel"
                      autoFocus
                      placeholder="Enter phone number"
                      value={forgotPhoneNumber}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        setForgotPhoneNumber(digits);
                        setError('');
                      }}
                      error={!!error}
                      variant="outlined"
                      size={isMobile ? 'small' : 'medium'}
                      inputProps={{ inputMode: 'numeric', maxLength: 15 }}
                    />
                  </Box>
                )}
              </CardContent>
              </Card>
            </>
          ) : (
            // Second screen - OTP and Password fields
            <>
              <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, bgcolor: 'primary.50', overflow: 'hidden' }}>
                <Box sx={{ bgcolor: 'primary.main', py: 1 }}>
                  <Typography color="white" variant={isMobile ? "body1" : "subtitle1"} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Verification Code Sent
                  </Typography>
                </Box>
                <CardContent sx={{ textAlign: 'center', p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <Typography color="text.secondary" variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                    We've sent a verification code to your {localStorage.getItem('contactType') || identifyInputType(formData.emailOrPhone).type}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'primary.light',
                      display: 'inline-block',
                      px: 2
                    }}
                  >
                    <Typography
                      color="text.primary"
                      variant="body2"
                      sx={{
                        fontWeight: 'medium',
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {maskContactInfo(
                        localStorage.getItem('contactType') || identifyInputType(formData.emailOrPhone).type,
                        localStorage.getItem('contactValue') || formData.emailOrPhone
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <TextField
                margin="normal"
                required
                fullWidth
                id="otp"
                label="Verification Code"
                name="otp"
                autoComplete="one-time-code"
                value={formData.otp}
                onChange={handleChange}
                error={!!error && error.includes('OTP')}
                inputProps={{ maxLength: 6 }}
                variant="outlined"
                sx={{ mb: 1 }}
                size={isMobile ? "small" : "medium"}
              />

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <OtpTimer onResend={handleResendOTP} isResending={isResending} initialTime={30} />
              </Box>
              <div className="otp-resend-message" style={{ minHeight: '24px', textAlign: 'center', marginBottom: '16px' }}></div>

              <Divider sx={{ my: isMobile ? 1 : 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                  New Password
                </Typography>
              </Divider>

              <TextField
                margin="normal"
                required
                fullWidth
                id="newPassword"
                label="New Password"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                error={!!passwordValidation.message && !passwordValidation.isValid}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        size={isMobile ? "small" : "medium"}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize={isMobile ? "small" : "medium"} /> : <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {formData.newPassword && (
                <FormHelperText
                  error={!passwordValidation.isValid}
                  sx={{ ml: 1.5, fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                >
                  {passwordValidation.message}
                </FormHelperText>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                id="confirmPassword"
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!error && error.includes('match')}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                        size={isMobile ? "small" : "medium"}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon fontSize={isMobile ? "small" : "medium"} /> : <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2, mb: 1, textAlign: 'center', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              py: isMobile ? 1 : 1.5,
              borderRadius: 2,
              fontWeight: 'bold',
              background: "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)",
              "&:hover": {
                background: "linear-gradient(90deg, #1565c0 0%, #1976d2 100%)",
              },
            }}
            disabled={isLoading}
            size={isMobile ? "medium" : "large"}
          >
            {isLoading ? (
              <CircularProgress size={isMobile ? 20 : 24} color="inherit" />
            ) : (
              otpSent ? 'Reset Password' : 'Send Verification Code'
            )}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Link
              href="#"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                onBack();
              }}
              sx={{ textDecoration: 'none', fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              Back to Login
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* Add Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ForgotPassword; 